import { VenueType, Venue, VenueBooking, PriceBreakdown } from '../types/venue';

/**
 * Format venue type to display string
 */
export const formatVenueType = (type: VenueType): string => {
  const typeMap: Record<VenueType, string> = {
    FUTBOL_5: 'Fútbol 5',
    FUTBOL_6: 'Fútbol 6',
    FUTBOL_7: 'Fútbol 7',
    FUTBOL_8: 'Fútbol 8',
    FUTBOL_9: 'Fútbol 9',
    FUTBOL_10: 'Fútbol 10',
    FUTBOL_11: 'Fútbol 11',
  };
  return typeMap[type] || type;
};

/**
 * Format price to Argentine Peso
 */
export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

/**
 * Format time string (HH:mm) to display format
 */
export const formatTime = (time: string): string => {
  return time; // Already in HH:mm format
};

/**
 * Format date string (YYYY-MM-DD) to display format
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-AR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

/**
 * Format short date (for cards)
 */
export const formatShortDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

/**
 * Calculate hours between two times
 */
export const calculateHours = (startTime: string, endTime: string): number => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  return (endMinutes - startMinutes) / 60;
};

/**
 * Check if time is peak hour (typically 18:00-22:00)
 */
export const isPeakHour = (time: string): boolean => {
  const hour = parseInt(time.split(':')[0]);
  return hour >= 18 && hour < 22;
};

/**
 * Calculate price breakdown for a booking
 */
export const calculatePrice = (
  venue: Venue,
  startTime: string,
  endTime: string
): PriceBreakdown => {
  const hours = calculateHours(startTime, endTime);
  const rate =
    isPeakHour(startTime) && venue.peakHourRate
      ? venue.peakHourRate
      : venue.hourlyRate;
  const subtotal = hours * rate;

  return {
    hours,
    rate,
    subtotal,
    serviceFee: 0,
    total: subtotal,
  };
};

/**
 * Get venue amenities as a readable list
 */
export const getVenueAmenities = (venue: Venue): string[] => {
  const amenities: string[] = [];

  if (venue.isIndoor) amenities.push('Techado');
  if (venue.hasLighting) amenities.push('Iluminación');
  if (venue.hasBathrooms) amenities.push('Baños');
  if (venue.hasParking) amenities.push('Estacionamiento');
  if (venue.hasChangingRooms) amenities.push('Vestuarios');

  // Add custom amenities
  if (venue.amenities && venue.amenities.length > 0) {
    amenities.push(...venue.amenities);
  }

  return amenities;
};

/**
 * Get venue status color
 */
export const getVenueStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    ACTIVE: '#00E676',
    PENDING_APPROVAL: '#FFD600',
    SUSPENDED: '#FF1744',
    REJECTED: '#FF1744',
  };
  return colorMap[status] || '#B0BEC5';
};

/**
 * Get booking status color
 */
export const getBookingStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    PENDING: '#FFD600',
    CONFIRMED: '#00E676',
    COMPLETED: '#B0BEC5',
    CANCELLED: '#FF1744',
    NO_SHOW: '#FF1744',
  };
  return colorMap[status] || '#B0BEC5';
};

/**
 * Get booking status text in Spanish
 */
export const getBookingStatusText = (status: string): string => {
  const textMap: Record<string, string> = {
    PENDING: 'Pendiente de pago',
    CONFIRMED: 'Confirmada',
    COMPLETED: 'Completada',
    CANCELLED: 'Cancelada',
    NO_SHOW: 'No asistió',
  };
  return textMap[status] || status;
};

/**
 * Validate Google Maps URL
 */
export const isValidMapsUrl = (url: string): boolean => {
  return (
    url.includes('google.com/maps') ||
    url.includes('goo.gl/maps') ||
    url.includes('maps.app.goo.gl')
  );
};

/**
 * Validate time format (HH:mm)
 */
export const isValidTimeFormat = (time: string): boolean => {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
};

/**
 * Generate time slots for a day (e.g., 08:00, 09:00, ...)
 */
export const generateTimeSlots = (
  startHour: number = 8,
  endHour: number = 23,
  intervalMinutes: number = 60
): string[] => {
  const slots: string[] = [];

  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      if (hour === endHour && minute > 0) break;
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeString);
    }
  }

  return slots;
};

/**
 * Check if a booking can be cancelled (not within 24 hours of start time)
 */
export const canCancelBooking = (booking: VenueBooking): boolean => {
  if (booking.status !== 'PENDING' && booking.status !== 'CONFIRMED') {
    return false;
  }

  const now = new Date();
  const bookingDateTime = new Date(`${booking.bookingDate}T${booking.startTime}`);
  const hoursDifference = (bookingDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

  return hoursDifference > 24;
};

/**
 * Get day of week name in Spanish
 */
export const getDayOfWeekName = (dayIndex: number): string => {
  const days = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ];
  return days[dayIndex] || '';
};

/**
 * Filter available time slots based on existing bookings
 */
export const filterAvailableSlots = (
  allSlots: string[],
  existingBookings: VenueBooking[],
  selectedDate: string
): string[] => {
  return allSlots.filter((startTime) => {
    // Check if this slot conflicts with any existing booking
    const hasConflict = existingBookings.some((booking) => {
      if (booking.bookingDate !== selectedDate) return false;

      const slotStart = startTime;
      const slotEnd = addHoursToTime(startTime, 1); // Assuming 1-hour slots

      return (
        (slotStart >= booking.startTime && slotStart < booking.endTime) ||
        (slotEnd > booking.startTime && slotEnd <= booking.endTime) ||
        (slotStart <= booking.startTime && slotEnd >= booking.endTime)
      );
    });

    return !hasConflict;
  });
};

/**
 * Add hours to a time string
 */
export const addHoursToTime = (time: string, hours: number): string => {
  const [hour, minute] = time.split(':').map(Number);
  const totalMinutes = hour * 60 + minute + hours * 60;
  const newHour = Math.floor(totalMinutes / 60) % 24;
  const newMinute = totalMinutes % 60;
  return `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
};

/**
 * Get star rating display (e.g., "4.5 ⭐")
 */
export const formatRating = (rating?: number): string => {
  if (!rating) return 'Sin calificación';
  return `${rating.toFixed(1)} ⭐`;
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Get relative time (e.g., "Hace 2 horas")
 */
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `Hace ${diffMins} minutos`;
  if (diffHours < 24) return `Hace ${diffHours} horas`;
  if (diffDays < 30) return `Hace ${diffDays} días`;
  return formatShortDate(dateString);
};
